import { IndustryType } from "../codegen/graphql";
import type { DashboardWidget, WidgetInstance } from "../components/DashboardWidgetConfig";

/**
 * Pure access checks for dashboard widgets — kept out of `DashboardWidgetConfig`
 * so they stay unit-testable (that module pulls in the map widget, which Jest
 * cannot load).
 */

/**
 * The exchange quick action only makes sense for semiconductor assets — the
 * event matrix offers `ASSET_EXCHANGE` nowhere else. Global admins manage every
 * client and keep it regardless of their own industry type.
 */
export const canUseExchangeQuickAction = (industryType: IndustryType, roles: string[]): boolean =>
    industryType === IndustryType.SEMICONDUCTOR || roles.includes('globalAdmin');

/**
 * Whether a saved widget instance may still be shown. `widgetTypes` must already
 * be filtered for the user (see `getWidgetTypes`): the widget type has to be
 * offered, and for configurable widgets so has the stored option. An instance
 * without a stored option (legacy) falls back to the widget's default and stays.
 */
export const isWidgetInstanceAllowed = (
    instance: WidgetInstance,
    widgetTypes: DashboardWidget[],
): boolean => {
    const widget = widgetTypes.find(w => w.type === instance.type);
    if (!widget) return false;
    if (!widget.configurable || !widget.configOptions) return true;

    const value = instance.config?.[widget.configKey ?? 'kpiType'];
    if (typeof value !== 'string') return true;
    return widget.configOptions.some(opt => opt.value === value);
};
